import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { apiClient } from '../../services/api/base';

function getApiErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map((d: { msg?: string }) => d.msg).filter(Boolean).join(', ') || fallback;
    }
    return error.response?.data?.message || fallback;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

function normalizeUsersListResponse(data: { users?: unknown[]; items?: unknown[]; total?: number }) {
  const items = data.users ?? data.items ?? [];
  return { items, total: data.total ?? items.length };
}

function normalizeInstitutionsListResponse(data: { institutions?: unknown[]; items?: unknown[]; total?: number }) {
  const items = data.institutions ?? data.items ?? [];
  return { items, total: data.total ?? items.length };
}

// Admin-specific types
export interface User {
  id: string;
  email: string;
  name: string;
  is_active: boolean;
  is_superuser: boolean;
  institution_id: string | null;
  medical_id?: string;
  personal_id?: string;
  phone?: string;
  specialty?: string;
  role: 'superuser' | 'institution_admin' | 'doctor';
  created_at: string;
  updated_at: string;
  last_login?: string;
}

export interface Institution {
  id: string;
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  logo_url?: string;
  settings: Record<string, any>;
  user_count: number;
  patient_count: number;
  report_count: number;
  created_at: string;
  updated_at: string;
}

export interface AuditLog {
  id: string;
  user_id: string;
  user_email: string;
  action: string;
  resource_type: string;
  resource_id: string;
  details: Record<string, any>;
  ip_address: string;
  user_agent: string;
  timestamp: string;
}

export interface SystemStats {
  total_users: number;
  active_users: number;
  total_institutions: number;
  total_patients: number;
  total_reports: number;
  total_dicom_studies: number;
  storage_usage: {
    total_gb: number;
    dicom_gb: number;
    reports_gb: number;
  };
  performance_metrics: {
    avg_response_time: number;
    error_rate: number;
    uptime_percentage: number;
  };
  recent_activity: {
    logins_24h: number;
    reports_24h: number;
    uploads_24h: number;
  };
}

export interface AdminFilters {
  users: {
    search: string;
    institution: string | null;
    role: string | null;
    status: 'all' | 'active' | 'inactive';
  };
  institutions: {
    search: string;
    sortBy: 'name' | 'created_at' | 'user_count';
    sortOrder: 'asc' | 'desc';
  };
  auditLogs: {
    user: string | null;
    action: string | null;
    resource_type: string | null;
    dateRange: [string, string] | null;
  };
}

export interface AdminState {
  // Users management
  users: User[];
  totalUsers: number;
  selectedUser: User | null;
  
  // Institutions management
  institutions: Institution[];
  totalInstitutions: number;
  selectedInstitution: Institution | null;
  
  // System monitoring
  systemStats: SystemStats | null;
  auditLogs: AuditLog[];
  totalAuditLogs: number;
  
  // UI state
  filters: AdminFilters;
  currentPage: number;
  pageSize: number;
  
  // Loading states
  isLoading: boolean;
  isLoadingUsers: boolean;
  isLoadingInstitutions: boolean;
  isLoadingStats: boolean;
  isLoadingLogs: boolean;
  isCreatingUser: boolean;
  isUpdatingUser: boolean;
  isDeletingUser: boolean;
  isTogglingVerification: boolean;
  isTogglingSuperuser: boolean;
  
  // Success message
  successMessage: string | null;

  // Error handling
  error: string | null;
  userError: string | null;
  institutionError: string | null;
}

const initialFilters: AdminFilters = {
  users: {
    search: '',
    institution: null,
    role: null,
    status: 'all',
  },
  institutions: {
    search: '',
    sortBy: 'name',
    sortOrder: 'asc',
  },
  auditLogs: {
    user: null,
    action: null,
    resource_type: null,
    dateRange: null,
  },
};

const initialState: AdminState = {
  users: [],
  totalUsers: 0,
  selectedUser: null,
  institutions: [],
  totalInstitutions: 0,
  selectedInstitution: null,
  systemStats: null,
  auditLogs: [],
  totalAuditLogs: 0,
  filters: initialFilters,
  currentPage: 1,
  pageSize: 20,
  isLoading: false,
  isLoadingUsers: false,
  isLoadingInstitutions: false,
  isLoadingStats: false,
  isLoadingLogs: false,
  isCreatingUser: false,
  isUpdatingUser: false,
  isDeletingUser: false,
  isTogglingVerification: false,
  isTogglingSuperuser: false,
  successMessage: null,
  error: null,
  userError: null,
  institutionError: null,
};

// Async thunks for admin operations
export const fetchUsers = createAsyncThunk(
  'admin/fetchUsers',
  async (params: { page?: number; size?: number; filters?: any }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/api/v1/admin/users', {
        params: {
          page: params.page || 1,
          size: params.size || 20,
          ...params.filters,
        },
      });
      return normalizeUsersListResponse(response.data);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch users'));
    }
  }
);

export const fetchInstitutions = createAsyncThunk(
  'admin/fetchInstitutions',
  async (params: { page?: number; size?: number; filters?: any }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/api/v1/admin/institutions', {
        params: {
          page: params.page || 1,
          size: params.size || 20,
          ...params.filters,
        },
      });
      return normalizeInstitutionsListResponse(response.data);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch institutions'));
    }
  }
);

export const fetchSystemStats = createAsyncThunk(
  'admin/fetchSystemStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/v1/admin/stats');
      if (!response.ok) throw new Error('Failed to fetch system stats');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const fetchAuditLogs = createAsyncThunk(
  'admin/fetchAuditLogs',
  async (params: { page?: number; size?: number; filters?: any }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams({
        page: (params.page || 1).toString(),
        size: (params.size || 50).toString(),
        ...params.filters,
      });
      
      const response = await fetch(`/api/v1/admin/audit-logs?${queryParams}`);
      if (!response.ok) throw new Error('Failed to fetch audit logs');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const updateUser = createAsyncThunk(
  'admin/updateUser',
  async (params: { userId: string; data: Partial<User> }, { rejectWithValue }) => {
    try {
      const response = await fetch(`/api/v1/admin/users/${params.userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params.data),
      });
      if (!response.ok) throw new Error('Failed to update user');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

export const createUserThunk = createAsyncThunk(
  'admin/createUser',
  async (userData: any, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/api/v1/admin/users', userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create user'));
    }
  }
);

export const updateUserThunk = createAsyncThunk(
  'admin/updateUserThunk',
  async (params: { userId: string; userData: any }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`/api/v1/admin/users/${params.userId}`, params.userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update user'));
    }
  }
);

export const fetchAllUsersAdmin = createAsyncThunk(
  'admin/fetchAllUsersAdmin',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/api/v1/admin/users', {
        params: { page: 1, size: 1000 },
      });
      return normalizeUsersListResponse(response.data);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'No se pudieron cargar los usuarios'));
    }
  }
);

export const deleteUserThunk = createAsyncThunk(
  'admin/deleteUser',
  async (userId: string, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/api/v1/admin/users/${userId}`);
      return userId;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete user'));
    }
  }
);

export const toggleUserVerification = createAsyncThunk(
  'admin/toggleVerification',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/api/v1/admin/users/${userId}/toggle-verification`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to toggle verification'));
    }
  }
);

export const toggleUserSuperuser = createAsyncThunk(
  'admin/toggleSuperuser',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/api/v1/admin/users/${userId}/toggle-superuser`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to toggle superuser'));
    }
  }
);

export const resetUserPasswordThunk = createAsyncThunk(
  'admin/resetPassword',
  async (params: { userId: string; newPassword: string }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/api/v1/admin/users/${params.userId}/reset-password`, {
        new_password: params.newPassword,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to reset password'));
    }
  }
);

export const updateInstitution = createAsyncThunk(
  'admin/updateInstitution',
  async (params: { institutionId: string; data: Partial<Institution> }, { rejectWithValue }) => {
    try {
      const response = await fetch(`/api/v1/admin/institutions/${params.institutionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params.data),
      });
      if (!response.ok) throw new Error('Failed to update institution');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    // User management
    selectUser: (state, action: PayloadAction<User | null>) => {
      state.selectedUser = action.payload;
    },
    
    clearUserError: (state) => {
      state.userError = null;
    },
    
    // Institution management
    selectInstitution: (state, action: PayloadAction<Institution | null>) => {
      state.selectedInstitution = action.payload;
    },
    
    clearInstitutionError: (state) => {
      state.institutionError = null;
    },
    
    // Filters management
    updateUserFilters: (state, action: PayloadAction<Partial<AdminFilters['users']>>) => {
      state.filters.users = { ...state.filters.users, ...action.payload };
    },
    
    updateInstitutionFilters: (state, action: PayloadAction<Partial<AdminFilters['institutions']>>) => {
      state.filters.institutions = { ...state.filters.institutions, ...action.payload };
    },
    
    updateAuditLogFilters: (state, action: PayloadAction<Partial<AdminFilters['auditLogs']>>) => {
      state.filters.auditLogs = { ...state.filters.auditLogs, ...action.payload };
    },
    
    resetFilters: (state) => {
      state.filters = initialFilters;
    },
    
    // Pagination
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    
    // Error handling
    clearError: (state) => {
      state.error = null;
    },
    
    clearAllErrors: (state) => {
      state.error = null;
      state.userError = null;
      state.institutionError = null;
    },

    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch users
      .addCase(fetchUsers.pending, (state) => {
        state.isLoadingUsers = true;
        state.userError = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.isLoadingUsers = false;
        state.users = action.payload.items;
        state.totalUsers = action.payload.total;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.isLoadingUsers = false;
        state.userError = action.payload as string;
      })
      
      // Fetch institutions
      .addCase(fetchInstitutions.pending, (state) => {
        state.isLoadingInstitutions = true;
        state.institutionError = null;
      })
      .addCase(fetchInstitutions.fulfilled, (state, action) => {
        state.isLoadingInstitutions = false;
        state.institutions = action.payload.items;
        state.totalInstitutions = action.payload.total;
      })
      .addCase(fetchInstitutions.rejected, (state, action) => {
        state.isLoadingInstitutions = false;
        state.institutionError = action.payload as string;
      })
      
      // Fetch system stats
      .addCase(fetchSystemStats.pending, (state) => {
        state.isLoadingStats = true;
        state.error = null;
      })
      .addCase(fetchSystemStats.fulfilled, (state, action) => {
        state.isLoadingStats = false;
        state.systemStats = action.payload;
      })
      .addCase(fetchSystemStats.rejected, (state, action) => {
        state.isLoadingStats = false;
        state.error = action.payload as string;
      })
      
      // Fetch audit logs
      .addCase(fetchAuditLogs.pending, (state) => {
        state.isLoadingLogs = true;
        state.error = null;
      })
      .addCase(fetchAuditLogs.fulfilled, (state, action) => {
        state.isLoadingLogs = false;
        state.auditLogs = action.payload.items;
        state.totalAuditLogs = action.payload.total;
      })
      .addCase(fetchAuditLogs.rejected, (state, action) => {
        state.isLoadingLogs = false;
        state.error = action.payload as string;
      })
      
      // Update user
      .addCase(updateUser.fulfilled, (state, action) => {
        const index = state.users.findIndex(user => user.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
        if (state.selectedUser?.id === action.payload.id) {
          state.selectedUser = action.payload;
        }
      })
      
      // Create user
      .addCase(createUserThunk.pending, (state) => {
        state.isCreatingUser = true;
        state.userError = null;
      })
      .addCase(createUserThunk.fulfilled, (state) => {
        state.isCreatingUser = false;
      })
      .addCase(createUserThunk.rejected, (state, action) => {
        state.isCreatingUser = false;
        state.userError = action.payload as string;
      })

      // Update user (thunk)
      .addCase(updateUserThunk.pending, (state) => {
        state.isUpdatingUser = true;
        state.userError = null;
      })
      .addCase(updateUserThunk.fulfilled, (state) => {
        state.isUpdatingUser = false;
      })
      .addCase(updateUserThunk.rejected, (state, action) => {
        state.isUpdatingUser = false;
        state.userError = action.payload as string;
      })

      // Fetch all users (admin)
      .addCase(fetchAllUsersAdmin.pending, (state) => {
        state.isLoadingUsers = true;
        state.userError = null;
      })
      .addCase(fetchAllUsersAdmin.fulfilled, (state, action) => {
        state.isLoadingUsers = false;
        state.users = action.payload.items;
        state.totalUsers = action.payload.total;
      })
      .addCase(fetchAllUsersAdmin.rejected, (state, action) => {
        state.isLoadingUsers = false;
        state.userError = action.payload as string;
      })

      // Delete user
      .addCase(deleteUserThunk.pending, (state) => {
        state.isDeletingUser = true;
        state.userError = null;
      })
      .addCase(deleteUserThunk.fulfilled, (state, action) => {
        state.isDeletingUser = false;
        state.users = state.users.filter(u => u.id !== action.payload);
        state.successMessage = 'Usuario eliminado exitosamente';
      })
      .addCase(deleteUserThunk.rejected, (state, action) => {
        state.isDeletingUser = false;
        state.userError = action.payload as string;
      })

      // Toggle verification
      .addCase(toggleUserVerification.pending, (state) => {
        state.isTogglingVerification = true;
        state.userError = null;
      })
      .addCase(toggleUserVerification.fulfilled, (state, action) => {
        state.isTogglingVerification = false;
        const user = action.payload?.user ?? action.payload;
        if (user?.id) {
          const index = state.users.findIndex(u => u.id === user.id);
          if (index !== -1) state.users[index] = { ...state.users[index], ...user };
        }
        state.successMessage = 'Verificación actualizada exitosamente';
      })
      .addCase(toggleUserVerification.rejected, (state, action) => {
        state.isTogglingVerification = false;
        state.userError = action.payload as string;
      })

      // Toggle superuser
      .addCase(toggleUserSuperuser.pending, (state) => {
        state.isTogglingSuperuser = true;
        state.userError = null;
      })
      .addCase(toggleUserSuperuser.fulfilled, (state, action) => {
        state.isTogglingSuperuser = false;
        const user = action.payload?.user ?? action.payload;
        if (user?.id) {
          const index = state.users.findIndex(u => u.id === user.id);
          if (index !== -1) state.users[index] = { ...state.users[index], ...user };
        }
        state.successMessage = 'Permisos actualizados exitosamente';
      })
      .addCase(toggleUserSuperuser.rejected, (state, action) => {
        state.isTogglingSuperuser = false;
        state.userError = action.payload as string;
      })

      // Reset password
      .addCase(resetUserPasswordThunk.fulfilled, (state) => {
        state.successMessage = 'Contraseña restablecida exitosamente';
      })
      .addCase(resetUserPasswordThunk.rejected, (state, action) => {
        state.userError = action.payload as string;
      })

      // Update institution
      .addCase(updateInstitution.fulfilled, (state, action) => {
        const index = state.institutions.findIndex(inst => inst.id === action.payload.id);
        if (index !== -1) {
          state.institutions[index] = action.payload;
        }
        if (state.selectedInstitution?.id === action.payload.id) {
          state.selectedInstitution = action.payload;
        }
      });
  },
});

export const {
  selectUser,
  clearUserError,
  selectInstitution,
  clearInstitutionError,
  updateUserFilters,
  updateInstitutionFilters,
  updateAuditLogFilters,
  resetFilters,
  setCurrentPage,
  setPageSize,
  clearError,
  clearAllErrors,
  clearSuccessMessage,
} = adminSlice.actions;

export default adminSlice.reducer;
